import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recipes as recipesAPI } from '../services/api';
import Avatar from '../components/Avatar';
import '../styles/Profile.css';

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [userRecipes, setUserRecipes] = useState([]);
  const [stats, setStats] = useState({
    recipesCount: 0,
    totalLikes: 0,
    favoritesCount: 0,
    totalViews: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    fetchUserData();
  }, [user, navigate, authLoading]);

  const fetchUserData = async () => {
    try {
      setLoading(true);

      // Fetch user's recipes
      const recipesResponse = await recipesAPI.getMyRecipes();
      const recipes = recipesResponse.data;
      setUserRecipes(recipes);

      // Calculate statistics
      const totalLikes = recipes.reduce((sum, recipe) => sum + (recipe.likes?.length || 0), 0);
      const totalViews = recipes.reduce((sum, recipe) => sum + (recipe.viewCount || 0), 0);

      setStats({
        recipesCount: recipes.length,
        totalLikes: totalLikes,
        favoritesCount: user.favoriteRecipes?.length || 0,
        totalViews: totalViews
      });

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  // Format date
  const memberSince = new Date(user.createdAt).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long'
  });

  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="container">
          <div className="profile-header-content">
            <Avatar
              name={user.name}
              imageUrl={user.profileImage}
              size="xlarge"
            />
            <div className="profile-header-info">
              <div className="profile-name-row">
                <h1 className="profile-name">
                  {user.nickname || user.name}
                  {user.nickname && (
                    <span className="profile-fullname">({user.name})</span>
                  )}
                </h1>
                {!user.isPublic && (
                  <span className="profile-badge badge-private">
                    🔒 Privát profil
                  </span>
                )}
                {user.isPublic && (
                  <span className="profile-badge badge-public">
                    🌐 Publikus profil
                  </span>
                )}
              </div>

              {user.bio && (
                <p className="profile-bio">{user.bio}</p>
              )}

              <div className="profile-meta">
                {user.city && (
                  <span className="profile-meta-item">
                    📍 {user.city}
                  </span>
                )}
                <span className="profile-meta-item">
                  📅 Tag {memberSince} óta
                </span>
              </div>

              <Link to="/settings" className="btn btn-primary">
                ⚙️ Profil szerkesztése
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="profile-stats">
        <div className="container">
          <div className="profile-stats-grid">
            <div className="profile-stat-card">
              <div className="profile-stat-icon">📖</div>
              <div className="profile-stat-value">{stats.recipesCount}</div>
              <div className="profile-stat-label">Receptek</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">❤️</div>
              <div className="profile-stat-value">{stats.totalLikes}</div>
              <div className="profile-stat-label">Like-ok</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">⭐</div>
              <div className="profile-stat-value">{stats.favoritesCount}</div>
              <div className="profile-stat-label">Kedvencek</div>
            </div>

            <div className="profile-stat-card">
              <div className="profile-stat-icon">👁️</div>
              <div className="profile-stat-value">{stats.totalViews}</div>
              <div className="profile-stat-label">Megtekintések</div>
            </div>
          </div>
        </div>
      </div>

      {/* User Recipes */}
      <div className="profile-recipes">
        <div className="container">
          <div className="profile-recipes-header">
            <h2>Receptjeim</h2>
            <Link to="/add-recipe" className="btn btn-outline">
              + Új recept
            </Link>
          </div>

          {loading ? (
            <div className="profile-loading">
              <p>Betöltés...</p>
            </div>
          ) : userRecipes.length === 0 ? (
            <div className="profile-empty">
              <p>Még nincs recepted. Hozz létre egyet!</p>
              <Link to="/add-recipe" className="btn btn-primary">
                + Első recept létrehozása
              </Link>
            </div>
          ) : (
            <div className="profile-recipes-grid">
              {userRecipes.map(recipe => (
                <Link
                  key={recipe._id}
                  to={`/recipes/${recipe._id}`}
                  className="profile-recipe-card"
                >
                  {recipe.coverImage && (
                    <div
                      className="profile-recipe-image"
                      style={{
                        backgroundImage: `url(${recipe.coverImage.medium || recipe.coverImage})`
                      }}
                    >
                      {!recipe.isApproved && (
                        <span className="profile-recipe-badge badge-pending">
                          ⏳ Jóváhagyásra vár
                        </span>
                      )}
                    </div>
                  )}
                  <div className="profile-recipe-content">
                    <h3 className="profile-recipe-title">{recipe.title}</h3>
                    <p className="profile-recipe-category">{recipe.category}</p>
                    <div className="profile-recipe-stats">
                      <span>❤️ {recipe.likes?.length || 0}</span>
                      <span>👁️ {recipe.viewCount || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
